import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { CmsService, GlobalMessageService, GlobalMessageType, OccEndpointsService, TranslationService } from '@spartacus/core';
import { Observable, Subscription, take, filter, map } from 'rxjs';
import { KNBR_MY_ACCOUNT_PRICE_LIST_ENTRY } from '../../../constants/api.endpoints.constant';
import { CustomerData, SalesArea } from '../../../feature-libs/kn-br-user/kn-br-account/root/model/customer.model';
import { KnBrCustomerContextService } from '../../../store/kn-br-customer-context/kn-br-customer-context.service';
import { KnBrCustomerContextAdapter } from '../../../store/kn-br-customer-context/kn-br-customer-context.adapter';
import { UserAccountService } from '../../../feature-libs/kn-br-user/kn-br-account/core/facade/user-account.service';
import { KnBrDateFormatService } from 'src/shared/kn-br-date-format.service';

interface PriceListRow {
  customerName: string;
  selectedSalesArea: string | null;
  salesAreaOptions: { label: string; value: string }[];
  number: string;
  dateForm: FormGroup;
  hasData: boolean;
}

@Component({
  selector: 'knbr-atg-price-list',
  templateUrl: './atg-price-list.component.html',
  styleUrl: './atg-price-list.component.scss',
  standalone: false
})
export class AtgPriceListComponent implements OnInit, OnDestroy {
  isPriceListAllowed = false;
  tableRows: PriceListRow[] = [];
  isSubmitRequestLoading = false;
  submitRequestSuccessMessage = '';
  minDate: NgbDateStruct = this.getTodayDateStruct();
  pageTitle$: Observable<string> = this.cmsService.getCurrentPage().pipe(map((page) => page?.title));
  private notAvailableValue = '';
  private currentUserEmail = '';

  private customerContextSubscription?: Subscription;

  constructor(
    private knBrCustomerContextService: KnBrCustomerContextService,
    private knBrCustomerContextAdapter: KnBrCustomerContextAdapter,
    private httpClient: HttpClient,
    private endPointService: OccEndpointsService,
    private translationService: TranslationService,
    private globalMessageService: GlobalMessageService,
    private userAccountService: UserAccountService,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    protected knBrDateFormatService: KnBrDateFormatService,
    private cmsService: CmsService
  ) {}

  ngOnInit(): void {
    this.isPriceListAllowed = true;
    this.initializeComponentData();
  }

  private initializeComponentData(): void {
    this.translationService
      .translate('atgPriceList.notAvailable')
      .pipe(take(1))
      .subscribe((value) => (this.notAvailableValue = value));

    this.userAccountService
      .get()
      .pipe(
        filter((user: any) => user && !!user.displayUid),
        take(1),
        map((user: any) => user.displayUid)
      )
      .subscribe((displayUid: string) => {
        this.currentUserEmail = displayUid || this.notAvailableValue;
      });

    this.customerContextSubscription = this.knBrCustomerContextService.get$.subscribe((customer: CustomerData) => {
      this.tableRows = customer && customer.customerNumber ? [this.getDataRow(customer)] : [];
      this.cdr.detectChanges();
    });
  }

  onSubmitNewRequest(row: PriceListRow): void {
    const dateValue = this.getRowDate(row);
    if (!row?.number || !this.hasValidSalesArea(row.selectedSalesArea) || !dateValue) {
      return;
    }

    this.submitRequestSuccessMessage = '';
    this.isSubmitRequestLoading = true;

    const payload = {
      customerName: this.currentUserEmail || this.notAvailableValue,
      salesArea: row.selectedSalesArea,
      number: row.number,
      date: this.formatDateForPayload(dateValue),
    };

    const url = this.endPointService.buildUrl(KNBR_MY_ACCOUNT_PRICE_LIST_ENTRY, {
      urlParams: {
        customerId: row.number,
      },
    });

    this.httpClient.post(url, payload).subscribe(
      (response: any) => {
        const isRequestRejected = response?.success === false || response?.success === 'false';

        if (isRequestRejected) {
          this.globalMessageService.add(
            response?.message || { key: 'knBrMessages.error' },
            GlobalMessageType.MSG_TYPE_ERROR
          );
          this.isSubmitRequestLoading = false;
          this.cdr.detectChanges();
          return;
        }

        this.translationService
          .translate('atgPriceList.submitRequestSuccessMessage', { number: row.number })
          .pipe(take(1))
          .subscribe((message) => {
            this.submitRequestSuccessMessage = message;
            this.isSubmitRequestLoading = false;
            this.cdr.detectChanges();
          });
      },
      (error: any) => {
        this.globalMessageService.remove(GlobalMessageType.MSG_TYPE_ERROR);
        this.globalMessageService.add(
          error?.error?.message || { key: 'knBrMessages.error' },
          GlobalMessageType.MSG_TYPE_ERROR
        );
        this.isSubmitRequestLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  get isAnyActionLoading(): boolean {
    return this.isSubmitRequestLoading;
  }

  closeSubmitRequestSuccessPopup(): void {
    this.submitRequestSuccessMessage = '';
  }

  hasValidSalesArea(value: string | null): boolean {
    return !!value && value !== this.notAvailableValue;
  }

  getRowDate(row: PriceListRow): NgbDateStruct | null {
    return row.dateForm.get('date')?.value ?? null;
  }

  ngOnDestroy(): void {
    this.customerContextSubscription?.unsubscribe();
  }

  private getTodayDateStruct(): NgbDateStruct {
    const today = new Date();
    return {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate(),
    };
  }

  private formatDateForPayload(date: NgbDateStruct): string {
    const month = String(date.month).padStart(2, '0');
    const day = String(date.day).padStart(2, '0');
    return `${month}/${day}/${date.year}`;
  }

  private getDataRow(customer: CustomerData): PriceListRow {
    const salesAreas: SalesArea[] = customer.salesAreaList || [];
    const salesAreaOptions =
      salesAreas.length > 0
        ? salesAreas.map((salesArea) => ({
            label: salesArea.accountName || salesArea.salesArea || this.notAvailableValue,
            value: salesArea.salesArea || this.notAvailableValue,
          }))
        : [{ label: this.notAvailableValue, value: this.notAvailableValue }];

    const storedSalesArea = this.knBrCustomerContextAdapter.getSalesArea();
    const selectedSalesArea: string | null = salesAreas.some((salesArea) => salesArea.salesArea === storedSalesArea)
      ? storedSalesArea
      : null;

    return {
      customerName: customer.customerName || this.notAvailableValue,
      selectedSalesArea,
      salesAreaOptions,
      number: customer.customerNumber || this.notAvailableValue,
      dateForm: this.fb.group({ date: [null] }),
      hasData: true,
    };
  }

}
